import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["data-cy"]
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = {
  key: 1,
  class: "detail-container"
}
const _hoisted_4 = { class: "title" }
const _hoisted_5 = { class: "description" }
const _hoisted_6 = { class: "size-description" }
const _hoisted_7 = { class: "format-description" }
const _hoisted_8 = { class: "max-size-description" }
const _hoisted_9 = { class: "button" }
const _hoisted_10 = ["accept"]

import NotifyService from "@/services/notify.service";
import { VButton, VIconSvg, VModalMediaAssets } from "@/components";
import VErrorTooltip from "../VErrorTooltip/VErrorTooltip.vue";
import { pick } from "lodash";
import { computed, watch, ref } from "vue";
import i18n from "@/plugins/vue-i18n";
import store from "@/store";

interface Props {
  value?: {
    id: number | null;
    originName: string;
    url: string;
    width: number;
    height: number;
  };
  mediaAssetId?: number | null;
  size?: string;
  maxWeight?: number;
  allowedFileTypes?: Array<string>;
  width?: number;
  height?: number;
  sizeCheckRule?: "equal" | "greaterThanOrEqual";
  required?: boolean;
  error?: string;

  side?: "advert" | "admin";
  userId?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VUploadImage',
  props: {
    value: { default: () => ({
    id: null,
    originName: "",
    url: "",
    width: 0,
    height: 0,
  }) },
    mediaAssetId: { default: null },
    size: { default: "md" },
    maxWeight: { default: 5 },
    allowedFileTypes: { default: () => ["jpg", "jpeg", "png"] },
    width: { default: 0 },
    height: { default: 0 },
    sizeCheckRule: { default: "equal" },
    required: { type: Boolean, default: false },
    error: { default: "" },
    side: { default: "advert" },
    userId: { default: undefined }
  },
  emits: ["input"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const file = ref();

const props = __props;

const image = computed({
  get() {
    return props.value;
  },

  set(value) {
    emit("input", value);
  },
});

const isExistImage = computed(() => {
  return !!Object.keys(image.value).length;
});

const isSizeMedium = computed(() => {
  return props.size === "md";
});

const titleValue = computed(() => {
  return isSizeMedium.value ? i18n.t("title.image") : i18n.t("title.icon");
});

const fileTypesString = computed(() => {
  return props.allowedFileTypes.map((item) => `.${item}`).join(", ");
});

const primaryDescription = computed(() => {
  const minSize = `${props.width}x${props.height}`;
  const fileTypes = props.allowedFileTypes.join(", ");

  return isSizeMedium.value
    ? `${i18n.t("page.campaign.min")} ${minSize} ${fileTypes}`
    : `${minSize} px`;
});

const secondaryDescription = computed(() => {
  return isSizeMedium.value
    ? i18n.t("component.imageUpload.description")
    : props.allowedFileTypes.join(", ");
});

const maxSizeDescription = computed(() => {
  return isSizeMedium.value ? i18n.t("component.imageUpload.maxSizeMessage") : "";
});

const containerClasses = computed(() => {
  return {
    "upload-image-wrapper-error": props.error,
    "upload-image-wrapper-required": props.required,
    "upload-image-wrapper-size-md": props.size === "md",
    "upload-image-wrapper-size-sm": props.size === "sm",
  };
});

const onChangeMediaAssetId = async () => {
  const id = props.mediaAssetId;

  if (id && !isExistImage.value) {
    const data = await Promise.resolve(store.dispatch("media-asset/getMediaAsset", id));

    image.value = pick(data, ["id", "originName", "url", "width", "height"]);
  }
};

watch(() => props.mediaAssetId, onChangeMediaAssetId, { immediate: true });

const upload = async (file: File) => {
  if (await isValidImage(file)) {
    const data = await Promise.resolve(store.dispatch("media-asset/uploadImage", file));

    image.value = pick(data, ["id", "originName", "url", "width", "height"]);
  }
};

const createFile = (event: Event) => {
  const [file] = event.target.files;

  upload(file);

  file.value = null;
};

const isValidImage = async (file: File) => {
  const fileCheck = new Promise((resolve) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const fileWeight = (event.total / (1024 * 1024)).toFixed(2);
      let isValid = props.maxWeight >= fileWeight;

      if (!isValid) {
        NotifyService.notify({
          type: "error",
          text: i18n.t("component.notify.error.wrongFileWeight", { weight: props.maxWeight }),
          duration: NotifyService.DURATION_LONG,
        });
      }

      const newImage = new Image();

      newImage.src = event.target.result;

      newImage.onload = () => {
        const imageWidth = newImage.width;
        const imageHeight = newImage.height;

        switch (props.sizeCheckRule) {
          case "equal":
            isValid = imageWidth === props.width && imageHeight === props.height;
            break;
          case "greaterThanOrEqual":
            isValid = imageWidth >= props.width && imageHeight >= props.height;
            break;
        }

        if (!isValid) NotifyService.error("wrongImageSize");

        resolve(isValid);
      };
    };
  });

  return await Promise.resolve(fileCheck);
};

const removeFile = () => {
  image.value = {};
};

const onDrop = (event: DragEvent) => {
  if (!isExistImage.value) {
    event.stopPropagation();
    event.preventDefault();
    const [file] = event.dataTransfer.files;

    upload(file);
  }
};

// const onClickUpload = () => {
//   file.value.click();
// };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["upload-image-wrapper", containerClasses.value])
  }, [
    _createElementVNode("div", {
      class: "upload-image",
      "data-cy": `drag-and-drop-upload-image-${_ctx.size}`,
      onDragover: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
      onDrop: onDrop
    }, [
      (isExistImage.value)
        ? (_openBlock(), _createElementBlock("img", {
            key: 0,
            src: image.value.url,
            alt: image.value.originName,
            class: "image",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$refs.modalMediaAssets.show()))
          }, null, 8, _hoisted_2))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, _toDisplayString(titleValue.value), 1),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(primaryDescription.value), 1),
              _createElementVNode("span", _hoisted_7, _toDisplayString(secondaryDescription.value), 1),
              _createElementVNode("span", _hoisted_8, _toDisplayString(maxSizeDescription.value), 1)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(_unref(VButton), {
                text: _ctx.$t('button.upload'),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$refs.modalMediaAssets.show()))
              }, null, 8, ["text"])
            ]),
            _createElementVNode("input", {
              id: "file-input",
              ref_key: "file",
              ref: file,
              "data-cy": "file-input",
              hidden: "",
              type: "file",
              accept: fileTypesString.value,
              onChange: createFile
            }, null, 40, _hoisted_10)
          ])),
      (isExistImage.value)
        ? (_openBlock(), _createBlock(_unref(VButton), {
            key: 2,
            "data-cy": `upload-image-remove-button-${_ctx.size}`,
            variant: "secondary",
            class: "delete",
            onClick: removeFile
          }, {
            "icon-left": _withCtx(() => [
              _createVNode(_unref(VIconSvg), {
                name: "Delete",
                color: "fc-ink"
              })
            ]),
            _: 1
          }, 8, ["data-cy"]))
        : _createCommentVNode("", true)
    ], 40, _hoisted_1),
    _createVNode(_unref(VModalMediaAssets), {
      ref: "modalMediaAssets",
      "media-asset": image.value,
      "onUpdate:mediaAsset": _cache[3] || (_cache[3] = ($event: any) => ((image).value = $event)),
      "media-type": "image",
      "max-weight": props.maxWeight,
      "allowed-file-types": props.allowedFileTypes,
      width: props.width,
      height: props.height,
      "size-check-rule": props.sizeCheckRule,
      side: _ctx.side,
      "user-id": _ctx.userId
    }, null, 8, ["media-asset", "max-weight", "allowed-file-types", "width", "height", "size-check-rule", "side", "user-id"]),
    (_ctx.error)
      ? (_openBlock(), _createBlock(VErrorTooltip, {
          key: 0,
          error: _ctx.error
        }, null, 8, ["error"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})